import type { Settings } from './settings';

type Environment = Record<string, string | boolean | undefined>;

/**
 * Reads the deployment configuration once. This is the only place that touches `import.meta.env`. Defaults
 * target the real CDN service on its local port, signing people in through Beyond Accounts; the development
 * mock is an explicit opt-in (`npm run dev -- --mode mock`), never a fallback.
 */
export class Config {
	static #defaults = {
		api: 'http://cdn.beyond.localhost:8130',
		docs: 'http://localhost:5186',
		landing: 'http://localhost:5185',
		polling: 1500,
		timeout: 20000
	};

	/**
	 * Beyond Accounts through the service unless another adapter is named. `administration` is the simulated
	 * email sign-in of the development mock and the tests, and `supabase` is unvalidated: both only by name
	 */
	static #adapter(named: string): Settings['identity']['adapter'] {
		return named === 'administration' || named === 'supabase' ? named : 'platform';
	}

	/**
	 * @param environment Defaults to the build environment; tests pass their own
	 */
	static read(environment: Environment = import.meta.env): Settings {
		const text = (name: string) => {
			const value = environment[name];
			return typeof value === 'string' ? value.trim() : '';
		};
		const origin = (name: string, fallback: string) => (text(name) || fallback).replace(/\/+$/, '');
		const polling = Number(text('VITE_REALTIME_POLLING_MS'));
		const timeout = Number(text('VITE_API_TIMEOUT_MS'));
		const realtime = text('VITE_REALTIME_ADAPTER');
		const api = origin('VITE_API_ORIGIN', Config.#defaults.api);

		return {
			mode: text('MODE') || 'production',
			api: { origin: api, timeout: Number.isInteger(timeout) && timeout > 0 ? timeout : Config.#defaults.timeout },
			identity: {
				adapter: Config.#adapter(text('VITE_IDENTITY_ADAPTER')),
				service: origin('VITE_SERVICE_ORIGIN', api),
				authority: origin('VITE_AUTHORITY_ORIGIN', api),
				supabase: { url: origin('VITE_SUPABASE_URL', ''), key: text('VITE_SUPABASE_KEY') },
				returns: text('VITE_RETURN_ORIGINS')
					.split(',')
					.map(value => value.trim().replace(/\/+$/, ''))
					.filter(Boolean)
			},
			realtime: {
				adapter: realtime === 'polling' || realtime === 'supabase-broadcast' ? realtime : 'auto',
				polling: Number.isFinite(polling) && polling >= 250 ? polling : Config.#defaults.polling
			},
			links: {
				docs: origin('VITE_DOCS_ORIGIN', Config.#defaults.docs),
				landing: origin('VITE_LANDING_ORIGIN', Config.#defaults.landing),
				workspace: origin('VITE_WORKSPACE_ORIGIN', ''),
				delivery: origin('VITE_DELIVERY_ORIGIN', ''),
				accounts: origin('VITE_ACCOUNTS_ORIGIN', ''),
				projects: origin('VITE_PROJECTS_ORIGIN', ''),
				packages: origin('VITE_PACKAGES_ORIGIN', '')
			}
		};
	}
}
